import { UserSettings } from "@/config";
import type { InputValue } from "@/services/formInput";
import * as EmailValidator from "email-validator";
import { assertPrice, InvalidPriceException, isEmptyPrice } from "@/services/prices";
import { gettext } from "@/services/translation";
import { CollectionItemConfig } from "@/services/collection/item";

export class ValueNotValidException extends Error {}

// Validates international phone numbers per Packeta API specification.
// Accepts optional '+' or '00' prefix followed by a country-specific pattern.
// See: https://docs.packeta.com/docs/api-reference/phone-number-formats
// If you update this, don't forget to check the error message in validatePhone.
export const PhoneRegex = new RegExp(
  "^(?:00|\\+)?(?:" +
    "9710?[2-79]\\d{8}|" + // AE
    "43[0-9]{8,13}|" + // AT
    "32[0-9]{9,10}|" + // BE
    "359(?:2\\d{7}|8\\d{8}|[13-79]\\d{7,8})|" + // BG
    "41[0-9]{9}|" + // CH
    "86[0-9]{11,12}|" + // CN
    "420[0-9]{9}|" + // CZ
    "49[0-9]{7,13}|" + // DE
    "45[0-9]{8}|" + // DK
    "372[0-9]{7,8}|" + // EE
    "34[0-9]{9}|" + // ES
    "358[0-9]{7,10}|" + // FI
    "33[0-9]{9}|" + // FR
    "44[0-9]{4,10}|" + // GB
    "30[0-9]{10,11}|" + // GR
    "852[0-9]{4,10}|" + // HK
    "385[0-9]{8,9}|" + // HR
    "36[0-9]{8,9}|" + // HU
    "353[0-9]{8,9}|" + // IE
    "972\\d{9}|" + // IL
    "39[0-9]{8,12}|" + // IT
    "423[0-9]{7,9}|" + // LI
    "370[0-9]{8,9}|" + // LT
    "352[0-9]{4,11}|" + // LU
    "371[0-9]{7}|" + // LV
    "382[0-9]{6,8}|" + // ME
    "356[0-9]{8}|" + // MT
    "31[0-9]{9}|" + // NL
    "968[0-9]{9}|" + // OM
    "48[0-9]{9}|" + // PL
    "351[0-9]{9}|" + // PT
    "974[0-9]{8}|" + // QA
    "400?[1-9]\\d{8}|" + // RO
    "7[0-9]{10}|" + // RU
    "966[0-9]{9}|" + // SA
    "46[0-9]{7,12}|" + // SE
    "386[0-9]{8}|" + // SI
    "421[0-9]{9}|" + // SK
    "90\\d{10}|" + // TR
    "380[0-9]{7,9}|" + // UA
    "1[0-9]{7,10}" + // US
    ")$"
);

// If you update this, don't forget to check error message for this.
export const NicknameRegex = new RegExp(`^[-0-9a-zA-Z_]{${UserSettings.nicknameMinLength},${UserSettings.nicknameMaxLength}}$`);

/** @throws {ValueNotValidException} */
export const validatePublicUserName = (name: InputValue): void => {
  assertString(name, "name");

  const nameTrimmed = name.trim();
  const length = nameTrimmed.length;
  if (length > UserSettings.nameMaxLength) {
    const msg = gettext.$gettext("Jméno nesmí být delší než %{maxLength} znaků.", { maxLength: UserSettings.nameMaxLength });
    throw new ValueNotValidException(msg);
  }

  if (length < 1) {
    const msg = gettext.$gettext("Jméno musí musí být vyplněno.");
    throw new ValueNotValidException(msg);
  }
};

/** @throws {ValueNotValidException} */
export const validatePhone = (phone: InputValue): void => {
  assertString(phone, "phone");
  const phoneString = phone as string;

  if (!phoneString.match(PhoneRegex)) {
    const msg = gettext.$gettext("Vyplň platné mezinárodní telefonní číslo.");
    throw new ValueNotValidException(msg);
  }
};

/** @throws {ValueNotValidException} */
export const validateUserBio = (bio: InputValue): void => {
  assertString(bio, "bio");
  const bioString = bio as string;

  const length = bioString.length;
  if (length > UserSettings.bioMaxLength) {
    const msg = gettext.$gettext("Délka bia je omezena na %{maxLength} znaků.", { maxLength: UserSettings.bioMaxLength });
    throw new ValueNotValidException(msg);
  }
};

/** @throws {ValueNotValidException} */
export const validateEmail = (email: InputValue, allowEmptyValue = true): void => {
  assertString(email, "e-mail");

  const emailString = email as string;
  const isFilled = emailString.length > 0;
  const isValid = EmailValidator.validate(emailString);
  const fillValid = allowEmptyValue || isFilled;

  if (!fillValid || !isValid) {
    const msg = gettext.$gettext("Vyplň existující e-mailovou adresu.");
    throw new ValueNotValidException(msg);
  }
};

/** @throws {ValueNotValidException} */
export const validateNickame = (nickname: InputValue): void => {
  assertString(nickname, "nickname");
  const nicknameString = nickname as string;

  const length = nicknameString.length;
  if (length < UserSettings.nicknameMinLength || length > UserSettings.nicknameMaxLength) {
    const msg = gettext.$gettext("Přezdívka musí mít délku %{minLength}-%{maxLength} znaků.", { minLength: UserSettings.nicknameMinLength, maxLength: UserSettings.nicknameMaxLength });
    throw new ValueNotValidException(msg);
  }

  const isValid = nicknameString.match(NicknameRegex);
  if (!isValid) {
    const msg = gettext.$gettext("Přezdívka smí obsahovat pouze alfanumerické znaky (bez diakritiky) a pomlčku.");
    throw new ValueNotValidException(msg);
  }
};

/** @throws {ValueNotValidException} */
export const validateUrl = (url: InputValue): URL | void => {
  assertString(url, "URL");

  try {
    let urlString = url as string;
    const isFilled = urlString.length > 0;
    if (isFilled) {
      if (!urlString.startsWith("http")) {
        urlString = "https://" + urlString;
      }
      return new URL(urlString);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    const msg = gettext.$gettext("Vyplň validní URL.");
    throw new ValueNotValidException(msg);
  }
};

/**
 * Checks whether given string is valid URL and has certain domain.
 *
 * @throws {ValueNotValidException}
 */
export function validateDomainUrl(urlToValidate: InputValue, requestedDomain: string): void {
  const url = validateUrl(urlToValidate);
  if (url) {
    const domain = url.hostname.replace("www.", "");

    if (domain !== requestedDomain) {
      const msg = gettext.$gettext("Zadaná URL nemá požadovanou doménu.");
      throw new ValueNotValidException(msg);
    }
  }
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemNote(note: InputValue): void {
  const maxLength = CollectionItemConfig.noteMaxLength;
  const maxMsg = gettext.$gettext("Poznámka může být nejvýše %{maxLength} znaků dlouhá.", { maxLength });
  assertStringMaxLength(note, maxLength, maxMsg);
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemDescription(description: InputValue): void {
  const maxLength = CollectionItemConfig.descriptionMaxLength;
  const maxMsg = gettext.$gettext("Poznámka může být nejvýše %{maxLength} znaků dlouhá.", { maxLength });
  assertStringMaxLength(description, maxLength, maxMsg);
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemWhereStored(value: InputValue): void {
  const minLength = CollectionItemConfig.whereStoredMinLength;
  const maxLength = CollectionItemConfig.whereStoredMaxLength;
  const minMsg = gettext.$gettext('"Kde skladuji" musí být alespoň %{minLength} znaků dlouhé.', { minLength });
  const maxMsg = gettext.$gettext('"Kde skladuji" může být nejvýše %{maxLength} znaků dlouhé.', { maxLength });
  assertStringMaxLength(value, maxLength, maxMsg);
  validateStringMinLength(value, minLength, minMsg);
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemAcquisitionSource(value: InputValue): void {
  const minLength = CollectionItemConfig.acquisitionSourceMin;
  const maxLength = CollectionItemConfig.acquisitionSourceMax;
  const minMsg = gettext.$gettext('"Odkud získáno" musí být alespoň %{minLength} znaků dlouhé.', { minLength });
  const maxMsg = gettext.$gettext('"Odkud získáno" může být nejvýše %{maxLength} znaků dlouhé.', { maxLength });
  assertStringMaxLength(value, maxLength, maxMsg);
  validateStringMinLength(value, minLength, minMsg);
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemName(value: InputValue): void {
  const minLength = CollectionItemConfig.nameMinLength;
  const maxLength = CollectionItemConfig.nameMaxLength;
  const minMsg = gettext.$gettext("Název nebo ID předmětu se musí skládat z alespoň %{minLength} znaků.", { minLength });
  const maxMsg = gettext.$gettext("Název nebo ID předmětu nesmí být delší než %{maxLength} znaků.", { maxLength });
  assertStringMaxLength(value, maxLength, maxMsg);
  validateStringMinLength(value, minLength, minMsg);
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemAcquirePrice(value: InputValue): void {
  try {
    const price = assertPrice(value);
    if (isEmptyPrice(price)) {
      return;
    }
    const amount = price.price;
    const max = CollectionItemConfig.acquirePriceMax;
    const min = CollectionItemConfig.acquirePriceMin;
    const minMsg = gettext.$gettext("Pořizovací cena nesmí být nižší než %{min}", { min });
    const maxMsg = gettext.$gettext("Pořizovací cena nesmí být vyšší než %{max}", { max });
    assertNumberMin(amount, min, minMsg, true);
    assertNumberMax(amount, max, maxMsg, true);
  } catch (e: unknown) {
    if (e instanceof InvalidPriceException) {
      throw new ValueNotValidException();
    } else {
      throw e;
    }
  }
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemCurrentPrice(value: InputValue): void {
  const min = CollectionItemConfig.currentPriceMin;
  const max = CollectionItemConfig.currentPriceMax;
  const minMsg = gettext.$gettext("Orientační cena nesmí být nižší než %{min}", { min });
  const maxMsg = gettext.$gettext("Orientační cena nesmí být vyšší než %{max}.", { max });
  try {
    const isNullAllowed = false;
    const price = assertPrice(value);
    if (isEmptyPrice(price)) {
      throw new ValueNotValidException(minMsg);
    }
    const amount = price.price;
    assertNumberMin(amount, min, minMsg, isNullAllowed);
    assertNumberMax(amount, max, maxMsg, isNullAllowed);
  } catch (e: unknown) {
    if (e instanceof InvalidPriceException) {
      throw new ValueNotValidException(minMsg);
    } else {
      throw e;
    }
  }
}

/**
 * @todo This is a very poor validation. Check at least format.
 * @throws {ValueNotValidException}
 **/
export function validateCollectionItemAcquireDate(value: InputValue): void {
  if (typeof value !== "string" || value === "" || value === null) {
    const msg = gettext.$gettext("Vyplň platné datum.");
    throw new ValueNotValidException(msg);
  }
}

/** @throws {ValueNotValidException} */
export function validateCollectionItemPieces(value: InputValue): void {
  const isNullAllowed = false;
  const min = CollectionItemConfig.piecesMin;
  const max = CollectionItemConfig.piecesMax;
  const minMsg = gettext.$gettext("Zadejte minimálně jeden kus.");
  const maxMsg = gettext.$gettext("Hodnota nesmí být vyšší než %{max}.", { max });
  assertNumberMin(value, min, minMsg, isNullAllowed);
  assertNumberMax(value, max, maxMsg, isNullAllowed);
}

export function assertString(value: unknown, valueName = "value"): asserts value is string {
  const type = typeof value;
  if (type !== "string") {
    throw new ValueNotValidException(`Type of "${valueName}" is "${type}", should be string.`);
  }
}

export function validateStringMinLength(value: InputValue, min: number, msg: string) {
  assertString(value);
  if ((value as string).length < min) {
    throw new ValueNotValidException(msg);
  }
}

function assertStringMaxLength(value: InputValue, max: number, msg: string) {
  assertString(value);
  if ((value as string).length > max) {
    throw new ValueNotValidException(msg);
  }
}

function assertNumber(value: InputValue): void {
  if (typeof value !== "number") {
    throw new ValueNotValidException(`DEV: Should not happen. Expected number.`);
  }
}

function assertNumberMin(value: InputValue, min: number, msg: string, allowNull = true) {
  if (allowNull && value === null) {
    return;
  }
  if (!allowNull && value === null) {
    throw new ValueNotValidException(msg);
  }

  assertNumber(value);
  if ((value as number) < min) {
    throw new ValueNotValidException(msg);
  }
}

function assertNumberMax(value: InputValue, max: number, msg: string, allowNull = true) {
  if (allowNull && value === null) {
    return;
  }
  if (!allowNull && value === null) {
    throw new ValueNotValidException(msg);
  }

  assertNumber(value);
  if ((value as number) > max) {
    throw new ValueNotValidException(msg);
  }
}
